import { useMemo, useState } from "react";
import type { PaginationState } from "@tanstack/react-table";

import { pageCount } from "@/core/api/pagination";
import { FLASHPAY_ROLES, RequireRole } from "@/core/auth";
import { DataTableClar } from "@/core/components/shared/data-table";
import { useNotificationLogs } from "@/features/notification/api/use-notification-logs";
import type { NotificationLogFilter } from "@/features/notification/api/dto";
import {
  NotificationLogsFilters,
  notificationLogsColumns,
  type NotificationLogsFiltersValue,
} from "@/features/notification/components";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";

/**
 * Historique paginé des envois (`POST /NotificationLogs/all` — les listes
 * sont en POST, guide §4.2). Barre externe pour les filtres statut/canal/partyId.
 * Rôles : flashpay-admin, flashpay-superadmin, flashpay-service.
 */
export function NotificationLogsPage() {
  const { t } = useNotificationsTranslation();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });
  const [filters, setFilters] = useState<NotificationLogsFiltersValue>({});

  const params = useMemo(
    () => ({
      filter: {
        status: filters.status,
        channel: filters.channel,
        partyId: filters.partyId,
      } satisfies NotificationLogFilter,
      page: pagination.pageIndex + 1,
      pageSize: pagination.pageSize,
    }),
    [filters, pagination]
  );

  const { data, isLoading, isFetching, error, refetch } =
    useNotificationLogs(params);

  return (
    <RequireRole
      roles={[
        FLASHPAY_ROLES.SUPERADMIN,
        FLASHPAY_ROLES.ADMIN,
        FLASHPAY_ROLES.SERVICE,
      ]}
    >
      <section className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold text-foreground">
            {t("notifications.logs.title")}
          </h1>
          <p className="max-w-2xl text-sm text-muted-foreground">
            {t("notifications.logs.subtitle")}
          </p>
        </div>

        <NotificationLogsFilters
          value={filters}
          onChange={(value) => {
            setFilters(value);
            setPagination((prev) => ({ ...prev, pageIndex: 0 }));
          }}
        />

        <DataTableClar
          data={data?.items ?? []}
          columnsClar={notificationLogsColumns}
          title={t("notifications.logs.title")}
          exportFileName="notification-logs"
          paginationMode="server"
          serverPageCount={data ? pageCount(data) : 0}
          totalItemsCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={setPagination}
          loading={{ isLoading, isFetching }}
          error={
            error
              ? { message: error.message, refetch: () => void refetch() }
              : undefined
          }
        />
      </section>
    </RequireRole>
  );
}
